import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Pagination from 'components/common-components/Pagination';
import MoreFieldsDropdown from 'components/common-components/MoreFieldsDropdown';
import FolderFilterToggler from 'components/common-components/FolderFilterToggler';
import TableFolderListFilter from 'pages/folders/TableFolderListFilter';
import { useTranslation } from 'react-i18next';
import Loader from 'components/common-components/Loader';
import NestedTableWithDragHeader from './NestedTableWithDragHeader';
import NestedTableWithDragData from './NestedTableWithDragData';

const NestedTableWithDragComponent = ({
  header,
  filterEnabled,
  data,
  totalRecords,
  loader,
  props,
  isFieldSelectionEnabled,
  isPaginationEnabled,
  isActionsEnabled,
  isSelectionEnabled,
  selectionKey,
  maintainTableHeight = true,
  maintainTableMaxHeight,
  paginationFunction,
  onSelection,
  selectedRows,
  selectedColumns,
  onColumnSelection,
  debounceFunc,
  filteringData,
  setFilteringData,
  tableHeight,
  noDataComponent,
  nestedTableProps,
}) => {
  let {
    type,
    isNested,
    isDragable,
    updateFilteringData,
    addRow,
    setTableHeaderChange,
    filteringHeader,
    actionsCallBack,
    editedRowData,
    actionsType,
    resetHeader,
    mediaEnabled,
    isAllRecordsSelected,
    folderFilterOpen,
    setFolderFilterOpen,
  } = { ...props };
  const { t } = useTranslation();

  const [tableHeaderTesting, setTableHeaderTesting] = useState([]);
  const [displayEntries, setDisplayEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setTableHeaderTesting(header);
  }, [header]);

  const changeDisplayEntries = (name, val) => {
    setDisplayEntries(parseInt(val));
    paginationFunction(1, parseInt(val));
  };

  return (
    <div className='d-flex'>
      <div className='flex-grow-1 overflow-auto position-relative'>
        {isFieldSelectionEnabled && (
          <div className={`position-absolute ${filterEnabled ? 'top-filter' : 'top-0'} end-0`}>
            <MoreFieldsDropdown
              fieldList={filteringHeader ? filteringHeader : tableHeaderTesting}
              setFieldListSelection={
                setTableHeaderChange ? setTableHeaderChange : setTableHeaderTesting
              }
              actionsType={actionsType}
              resetHeader={resetHeader}
            />
          </div>
        )}
        {type === 'form_list' && !folderFilterOpen && (
          <div className='position-absolute top-0 end-0'>
            <FolderFilterToggler onClick={() => setFolderFilterOpen(true)} />
          </div>
        )}
        <div
          className={`d-flex flex-column table-responsive${
            maintainTableHeight ? ' maintain-height' : ''
          }${maintainTableMaxHeight ? ' maintain-max-height' : ''}${
            type === 'submission_logs' ? ' submission-logs-table' : ''
          }`}
          style={{ height: tableHeight }}
        >
          <table
            className={`table-custom ${
              type === 'form_permissions'
                ? 'inverse'
                : type === 'userAccessedForms'
                ? 'bordered'
                : type === 'form_version'
                ? 'version'
                : type === 'data_sources_entries'
                ? 'entries'
                : type === 'profiles_list'
                ? 'profiles'
                : type === 'data_source_filter_list'
                ? 'table-bordered'
                : type === 'submission_logs'
                ? 'text-left'
                : type === 'dashboards'
                ? 'analytics'
                : ''
            } ${isNested ? 'nested' : ''} `}
          >
            <NestedTableWithDragHeader
              filterEnabled={filterEnabled}
              mediaEnabled={mediaEnabled ?? false}
              tableFields={tableHeaderTesting}
              onSelection={onSelection}
              selectedColumns={selectedColumns}
              onColumnSelection={onColumnSelection}
              isSelectionEnabled={isSelectionEnabled}
              isActionsEnabled={isActionsEnabled}
              selectionKey={selectionKey}
              selectionValue={
                isAllRecordsSelected === true || isAllRecordsSelected === false
                  ? isAllRecordsSelected
                  : selectedRows && selectedRows.length > 0
                  ? selectedRows.length === data.length
                  : false
              }
              debounceFunc={debounceFunc}
              filteringData={filteringData}
              setFilteringData={setFilteringData}
              addRow={addRow}
              closeRow={
                actionsCallBack && typeof actionsCallBack === 'function' ? actionsCallBack : null
              }
              editedRowData={editedRowData}
              type={type}
              nestedTableProps={nestedTableProps}
              isDragable={isDragable}
            />
            <NestedTableWithDragData
              onSelection={onSelection}
              data={data}
              tableFields={tableHeaderTesting}
              props={props}
              isSelectionEnabled={isSelectionEnabled}
              isActionsEnabled={isActionsEnabled}
              selectionKey={selectionKey}
              selectedRows={selectedRows}
              nestedTableProps={nestedTableProps}
            />
          </table>
          {loader && data && data.length === 0 && (
            <div className='d-flex flex-grow-1'>
              <div className='w-100 d-flex justify-content-center align-items-center'>
                <Loader color='green-1' />
              </div>
            </div>
          )}
          {!loader && data && data.length === 0 && (
            <div className='d-flex flex-grow-1'>
              {noDataComponent || (
                <span className='text-center align-self-center mx-auto'>
                  {t('text_no_records_found')}
                </span>
              )}
            </div>
          )}
        </div>
        {isPaginationEnabled && (
          <Pagination
            totalRecords={totalRecords}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            displayEntries={displayEntries}
            setDisplayEntries={changeDisplayEntries}
            currentPageRecords={data ? data.length : 0}
            paginationFunction={paginationFunction}
          />
        )}
      </div>
      {type === 'form_list' && folderFilterOpen && (
        <TableFolderListFilter
          closeFilter={() => setFolderFilterOpen(false)}
          filteringData={filteringData}
          updateFilteringData={updateFilteringData}
        />
      )}
    </div>
  );
};
NestedTableWithDragComponent.propTypes = {
  selectedRows: PropTypes.array,
  selectedColumns: PropTypes.array,
  onColumnSelection: PropTypes.func,
  header: PropTypes.array.isRequired,
  filterEnabled: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  totalRecords: PropTypes.number.isRequired,
  loader: PropTypes.bool.isRequired,
  props: PropTypes.object.isRequired,
  isFieldSelectionEnabled: PropTypes.bool.isRequired,
  isPaginationEnabled: PropTypes.bool.isRequired,
  isActionsEnabled: PropTypes.bool.isRequired,
  isSelectionEnabled: PropTypes.bool,
  selectionKey: PropTypes.string,
  maintainTableHeight: PropTypes.bool,
  maintainTableMaxHeight: PropTypes.bool,
  paginationFunction: PropTypes.func,
  onSelection: PropTypes.func,
  debounceFunc: PropTypes.func,
  filteringData: PropTypes.object,
  setFilteringData: PropTypes.func,
  tableHeight: PropTypes.string,
  noDataComponent: PropTypes.object,
  nestedTableProps: PropTypes.object,
};
export default NestedTableWithDragComponent;
